/**
 * /tenants — thin route shim.
 *
 * The route file is responsible for ONLY:
 *   - URL search schema
 *   - Loader (prefetch + revalidation)
 *   - Wiring the feature's page component
 *
 * All domain logic lives in `features/tenants/`.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  tenantsListSearchSchema,
  type TenantsListSearch,
} from "@/features/tenants/schemas";
import { fetchTenantsList } from "@/features/tenants/queries";
import { queryKeys } from "@/lib/query-keys";
import { sessionQueryKey, type AdminSession } from "@/queries/session";
import { TenantsListPage } from "@/features/tenants/pages/tenants-list-page";

export const Route = createFileRoute("/_authed/tenants/")({
  // The cross-tenant list is a platform surface. A tenant-scope
  // admin's "tenants" is exactly their own tenant, so any path that
  // lands them here (breadcrumbs on every tenant page link to
  // /tenants) redirects to their console instead of rendering a
  // one-row list with platform-only controls. Session is already
  // cached by the parent _authed.tsx beforeLoad.
  beforeLoad: ({ context }) => {
    const session = context.queryClient.getQueryData<AdminSession>(sessionQueryKey);
    if (session && !session.is_platform_admin && session.admin.tenant_slug) {
      throw redirect({
        to: "/tenants/$slug",
        params: { slug: session.admin.tenant_slug },
      });
    }
  },
  // Explicit return type is important — TanStack Router uses this to
  // derive `useSearch()` and `navigate({ search })` types all the way
  // down into the feature page. If the inference goes through Zod's
  // `.parse()` generic, TS resolves it to `never` under
  // verbatimModuleSyntax + noUncheckedIndexedAccess.
  validateSearch: (raw: Record<string, unknown>): TenantsListSearch =>
    tenantsListSearchSchema.parse(raw),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.tenants.list(deps.search),
      queryFn: () => fetchTenantsList(deps.search),
    }),
  component: TenantsListPage,
});
